import type { CompetitionFunnel, CompetitionFunnelStage } from '@/api/competitionDetail'
import { EmptyState } from '@/design'
import type { GlobalFilters } from '@/lib/filters'

import { NO_FIGURE, NOT_MEASURED } from '../../funnel/figures'
import { StagesTable, type StageRow } from '../../funnel/StagesTable'
import { TabRead } from '../pieces'
import { useCompetitionFunnel } from '../queries'
import { describeDetailReason, Reasoned } from '../reasons'
import '../../funnel/funnel.css'

/**
 * The competition's funnel over its measured sessions (`…/funnel`), read when the tab
 * opens: each stage's users and sessions, its conversion from the stage before and from
 * the first, and the drop-off on the way in. Visitors are never counted per competition,
 * a stage no session reached though a later one did is "Not measured" (its event is not
 * sent, so its 0 is no drop-off), and before tracking started every stage says why.
 */
export function FunnelTab({ competitionId, filters }: { competitionId: string; filters: GlobalFilters }) {
  const read = useCompetitionFunnel(competitionId, filters)
  return (
    <TabRead read={read} loading="Loading the funnel">
      {(funnel) => <FunnelBody funnel={funnel} />}
    </TabRead>
  )
}

/** Whether a stage has figures: it is neither unavailable nor without events. */
function measured(stage: CompetitionFunnelStage | undefined): stage is CompetitionFunnelStage {
  return stage !== undefined && stage.unavailable === null && !stage.withoutEvents && stage.sessions !== null
}

function FunnelBody({ funnel }: { funnel: CompetitionFunnel }) {
  const { stages, coverage } = funnel
  const counted = stages.filter((stage) => stage.key !== 'visitors')
  if (counted.length > 0 && counted.every((stage) => stage.unavailable === 'before_first_event')) {
    return <EmptyState variant="not-live" compact description={describeDetailReason('before_first_event').why} />
  }
  if (counted.every((stage) => (stage.sessions ?? 0) === 0)) {
    return (
      <>
        <EmptyState
          variant="no-results"
          compact
          title="No measured sessions for this competition"
          description="No measured session viewed or bought this competition in this period. Try a wider date range."
        />
        <p className="cd-note">{coverage.note}</p>
      </>
    )
  }
  const withoutEvents = stages.filter((stage) => stage.withoutEvents)

  return (
    <div className="cd-stack">
      <p className="cd-note">{coverage.note}</p>
      <StagesTable
        caption="Competition funnel stages"
        stages={stages}
        row={stageRow}
        header={(stage) => stage.label}
      />
      {withoutEvents.length > 0 ? (
        <p className="cd-note" role="note">
          Not measured: no session reached {withoutEvents.map((stage) => stage.label.toLowerCase()).join(', ')} although a later
          stage was reached, so its event is evidently not sent for this competition. Its 0 is not a drop-off.
        </p>
      ) : null}
    </div>
  )
}

/** A stage's row: its figures when it has them, else why not — its reason, or "Not measured". */
function stageRow(stage: CompetitionFunnelStage, above: CompetitionFunnelStage | undefined): StageRow {
  const has = measured(stage)
  // The first counted stage is the funnel's whole (its total conversion is null: the rates run from it).
  const first = above === undefined || above.key === 'visitors'
  return {
    key: stage.key,
    measured: has,
    // A step, or a loss, from a stage with no figure (or to one) would compare against a count nobody measured.
    comparable: has && measured(above),
    users: stage.users,
    sessions: stage.sessions,
    stageConversionRate: stage.stageConversionRate,
    totalConversionRate: stage.totalConversionRate,
    dropOff: stage.dropOff,
    dropOffRate: stage.dropOffRate,
    share: !has ? null : (stage.totalConversionRate ?? (first ? 1 : null)),
    missing: stage.unavailable ? (
      <Reasoned reason={stage.unavailable} />
    ) : stage.withoutEvents ? (
      <span className="ci-funnel__unmeasured">{NOT_MEASURED}</span>
    ) : (
      NO_FIGURE
    ),
  }
}
